/**
 * D-EZ-107 / D-EZ-108 — WHAT A RACE GAVE EACH MAN TO TALK ABOUT.
 *
 * Reads one settled race (and the league around it) and lists the quotable
 * facts: who won (his first? his tenth?), who ran second to whom, who was
 * wrecked by whom, who lost the lead late, whose motor let go, who came back
 * from an injury, who reached a milestone, who clinched or was knocked out of
 * the playoff, whose rival won. Every fact is a reading of the ledger — the
 * finishing order and grid, the rivalry book's incidents for this race, the
 * season's absences and bracket. `victoryQuotes` turns the winner's and one
 * rival's facts into words for Victory Lane.
 */

import type { SeasonRaceResult } from '../seasonPlay.ts';
import { MILESTONE_STARTS } from '../history/milestones.ts';
import type { HistorySeason } from '../history/source.ts';
import { REGULAR_SEASON_RACES } from '../schedule.ts';
import type { PeopleContext } from './context.ts';
import { quote, type Quote, type QuoteFact, type QuoteFactBody } from './quotes.ts';
import { relationshipsOf, type RivalryRecord } from './rivalry.ts';

export interface DriverFact {
  driverId: string;
  fact: QuoteFact;
}

export interface FactExtras {
  /** The primary sponsor on his car, when the books know it. */
  sponsorOf?: (id: string) => string | null;
  /** His shop's name; defaults to the season's garage. */
  teamOf?: (id: string) => string | null;
}

/** An out-of-the-race reason that is the car's, not a crash's. */
const MECHANICAL = new Set(['problem', 'fuel']);

function raceIdOf(run: SeasonRaceResult, seasonNumber: number): string {
  return run.race.state?.raceId ?? `s${seasonNumber}-r${run.raceNumber}`;
}

/** Running career counts (league) per driver, by race, built once per context. */
interface CareerIndex {
  /** `${season}|${race}` → driverId → [starts through, wins through]. */
  at: Map<string, Map<string, [number, number]>>;
}

const indexCache = new WeakMap<PeopleContext, CareerIndex>();

function careerIndex(ctx: PeopleContext): CareerIndex {
  const hit = indexCache.get(ctx);
  if (hit) return hit;
  const at = new Map<string, Map<string, [number, number]>>();
  const run = new Map<string, [number, number]>();
  for (const s of ctx.seasons) {
    for (const r of [...s.season.races].sort((x, y) => x.raceNumber - y.raceNumber)) {
      const snap = new Map<string, [number, number]>();
      for (const f of r.race.finishingOrder) {
        const c = run.get(f.driverId) ?? [0, 0];
        const next: [number, number] = [c[0] + 1, c[1] + (r.race.winnerId === f.driverId ? 1 : 0)];
        run.set(f.driverId, next);
        snap.set(f.driverId, next);
      }
      at.set(`${s.seasonNumber}|${r.raceNumber}`, snap);
    }
  }
  const idx = { at };
  indexCache.set(ctx, idx);
  return idx;
}

/** Every quotable fact this race produced, in a stable order. */
export function factsForRace(ctx: PeopleContext, seasonNumber: number, raceNumber: number, extras: FactExtras = {}): DriverFact[] {
  const season = ctx.seasons.find((s) => s.seasonNumber === seasonNumber);
  const run = season?.season.races.find((r) => r.raceNumber === raceNumber);
  if (!season || !run) return [];
  const raceId = raceIdOf(run, seasonNumber);
  const where = (id: string, occurrence?: number) => ({
    raceId,
    seasonNumber,
    raceNumber,
    sponsor: extras.sponsorOf?.(id) ?? null,
    team: extras.teamOf?.(id) ?? teamName(season, id),
    ...(occurrence !== undefined ? { occurrence } : {}),
  });
  const out: DriverFact[] = [];
  const push = (driverId: string, body: QuoteFactBody, occurrence?: number) =>
    out.push({ driverId, fact: { ...body, ...where(driverId, occurrence) } as QuoteFact });
  const order = run.race.finishingOrder;
  const winner = run.race.winnerId;
  const career = careerIndex(ctx).at.get(`${seasonNumber}|${raceNumber}`);

  if (winner) {
    const before = season.season.races.filter((r) => r.raceNumber < raceNumber && r.race.winnerId === winner).length;
    const wins = career?.get(winner)?.[1] ?? 0;
    push(winner, { kind: 'won', first: wins === 1, nth: wins }, before);
  }
  const second = order.find((f) => f.place === 2);
  if (second && winner) push(second.driverId, { kind: 'second', otherId: winner });
  for (const f of order) {
    if (f.place >= 3 && f.place <= 5 && f.status === 'running') push(f.driverId, { kind: 'good-run' });
  }

  // Incidents this race, from the rivalry book.
  const lostLate = new Set<string>();
  const wreckedBy = new Set<string>();
  for (const m of ctx.book.momentsByRace.get(`${seasonNumber}|${raceNumber}`) ?? []) {
    if (m.kind === 'wrecked' && m.aggressorId && m.victimId && m.detail !== 'self-out') {
      if (!wreckedBy.has(m.victimId)) {
        push(m.victimId, { kind: 'wrecked-by', otherId: m.aggressorId });
        wreckedBy.add(m.victimId);
      }
      push(m.aggressorId, { kind: 'wrecked', otherId: m.victimId });
    }
    if (m.kind === 'late-pass-win' && m.victimId && m.aggressorId) {
      push(m.victimId, { kind: 'lost-lead-late', otherId: m.aggressorId });
      lostLate.add(m.victimId);
    }
  }
  // The car let go; or a bad day — started in the top ten, finished outside the top twenty.
  for (const f of order) {
    if (f.status === 'out' && f.outReason && MECHANICAL.has(f.outReason)) {
      push(f.driverId, { kind: 'dnf-mechanical' });
      continue;
    }
    const started = run.startPositions[f.driverId];
    if (f.status === 'running' && f.place > 20 && started !== undefined && started <= 10 && !lostLate.has(f.driverId) && !wreckedBy.has(f.driverId)) {
      push(f.driverId, { kind: 'bad-day' });
    }
  }

  // Back from an injury: missed the race before with one, started this one.
  const prev = season.season.races.find((r) => r.raceNumber === raceNumber - 1);
  for (const a of prev?.absences ?? []) {
    if (a.reason !== 'injured' && a.reason !== 'season-injury') continue;
    if (order.some((f) => f.driverId === a.driverId)) push(a.driverId, { kind: 'injury-return' });
  }

  // Start milestones reached today.
  for (const f of order) {
    const starts = career?.get(f.driverId)?.[0] ?? 0;
    if (MILESTONE_STARTS.includes(starts)) push(f.driverId, { kind: 'milestone', what: 'starts', n: starts });
  }

  // The bracket: in on this race, through, or knocked out by it.
  const playoff = season.season.playoff;
  if (playoff) {
    if (raceNumber === REGULAR_SEASON_RACES) for (const s of playoff.seeds ?? []) push(s.driverId, { kind: 'clinched' });
    for (const round of playoff.rounds ?? []) {
      if (round.races[round.races.length - 1] !== raceNumber) continue;
      for (const id of round.advanced) push(id, { kind: 'clinched' });
      for (const id of round.eliminated) push(id, { kind: 'eliminated' });
    }
  }

  // Rivals who watched the other man win.
  if (winner) {
    for (const r of relationshipsOf(ctx.book, winner).rivals) {
      if (r.record.state !== 'boiling' && r.record.state !== 'hot') continue;
      if (!order.some((f) => f.driverId === r.other)) continue;
      push(r.other, { kind: 'rival-won', otherId: winner });
    }
  }
  return out;
}

function teamName(season: HistorySeason, id: string): string | null {
  return season.teams.find((t) => t.driverIds.includes(id))?.name ?? null;
}

export interface VictoryQuotes {
  winner: Quote | null;
  /** One other voice: his hottest rival in the race, else the runner-up. */
  other: { quote: Quote; rivalry: RivalryRecord | null } | null;
}

/** Victory Lane's two voices for race `raceNumber` of season `seasonNumber`. */
export function victoryQuotes(ctx: PeopleContext, seasonNumber: number, raceNumber: number, extras: FactExtras = {}): VictoryQuotes {
  const facts = factsForRace(ctx, seasonNumber, raceNumber, extras);
  const season = ctx.seasons.find((s) => s.seasonNumber === seasonNumber);
  const run = season?.season.races.find((r) => r.raceNumber === raceNumber);
  const winnerId = run?.race.winnerId ?? null;
  if (!winnerId) return { winner: null, other: null };
  const wf = facts.find((f) => f.driverId === winnerId && f.fact.kind === 'won');
  const winner = wf ? quote(wf.fact, winnerId, ctx) : null;

  const ran = new Set(run?.race.finishingOrder.map((f) => f.driverId) ?? []);
  const rivals = relationshipsOf(ctx.book, winnerId).rivals.filter(
    (r) => ran.has(r.other) && (r.record.state === 'boiling' || r.record.state === 'hot' || r.record.state === 'simmering'),
  );
  const PRIORITY = ['wrecked-by', 'lost-lead-late', 'second', 'rival-won'];
  for (const r of rivals) {
    const mine = facts.filter((f) => f.driverId === r.other && 'otherId' in f.fact && f.fact.otherId === winnerId);
    const best = PRIORITY.map((k) => mine.find((f) => f.fact.kind === k)).find(Boolean);
    const fact: QuoteFact = best?.fact ?? ({ ...(wf?.fact ?? {}), kind: 'rival-won', otherId: winnerId, occurrence: undefined } as QuoteFact);
    const q = quote(fact, r.other, ctx);
    if (q) return { winner, other: { quote: q, rivalry: r.record } };
  }
  const second = facts.find((f) => f.fact.kind === 'second');
  const q2 = second ? quote(second.fact, second.driverId, ctx) : null;
  return { winner, other: q2 ? { quote: q2, rivalry: null } : null };
}
